import {
  BACKEND_URL,
  BLACK_COLOR,
  PARK_RAZVLECHENIY_CODE,
  PROMENAD_CODE,
} from '@/constants';
import { Layout } from '@/layout';
import { SettingsType } from '@/types';
import { CommonTranslations } from '@/types/translations';
import { buildBreadcrumbsFromRoutes } from '@/utils/buildBreadcrumbs';
import { getCardRedirectUrl } from '@/utils/getCardRedirectUrl';
import { getTextColor } from '@/utils/getTextColor';
import { parseText } from '@/utils/parseText';
import cn from 'classnames';
import InnerHTML from 'dangerously-set-html-content';
import Link from 'next/link';
import { useEffect } from 'react';
import { BreadcrumbSchema } from '../BreadcrumbSchema';
import { ButtonType, CardSize, CardType, TagPosition } from '../Card';
import { EventsBlock } from '../EventsBlock';
import { Gallery } from '../Gallery';
import { LocationMap } from '../LocationMap';
import { ObjectParams } from '../ObjectParams';
import { PaginationRoutes } from '../PaginationRoutes';
import { ProductSchema } from '../ProductSchema';
import { MobileEffect, SliderBlock } from '../SliderBlock';
import { Tickets } from '../Tickets';
import styles from './TemplatePageContent.module.scss';

type TemplatePageContentProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
  menu: SettingsType;
  common: CommonTranslations;
};

enum BLOCK_ARRAY_ELEMENT {
  Mode = 'mode',
  Params = 'params',
  TextParams = 'textParams',
  Events = 'events',
  SpecialOffers = 'specialOffers',
  Menu = 'menu',
  Tickets = 'tickets',
  Map = 'map',
  ThingsToDo = 'thingsToDo',
}

const DEFAULT_BLOCK_ARRAY = [
  BLOCK_ARRAY_ELEMENT.Mode,
  BLOCK_ARRAY_ELEMENT.Params,
  BLOCK_ARRAY_ELEMENT.TextParams,
  BLOCK_ARRAY_ELEMENT.Events,
  BLOCK_ARRAY_ELEMENT.SpecialOffers,
  BLOCK_ARRAY_ELEMENT.Menu,
  BLOCK_ARRAY_ELEMENT.Tickets,
  BLOCK_ARRAY_ELEMENT.Map,
  BLOCK_ARRAY_ELEMENT.ThingsToDo,
];

const getBlockStructure = (blocks: string[]) => {
  if (blocks && Array.isArray(blocks)) {
    const blockMapping: { [key: string]: string } = {
      mode: BLOCK_ARRAY_ELEMENT.Mode,
      params: BLOCK_ARRAY_ELEMENT.Params,
      textParams: BLOCK_ARRAY_ELEMENT.TextParams,
      events: BLOCK_ARRAY_ELEMENT.Events,
      specialOffers: BLOCK_ARRAY_ELEMENT.SpecialOffers,
      menu: BLOCK_ARRAY_ELEMENT.Menu,
      tickets: BLOCK_ARRAY_ELEMENT.Tickets,
      map: BLOCK_ARRAY_ELEMENT.Map,
      thingsToDo: BLOCK_ARRAY_ELEMENT.ThingsToDo,
    };

    const orderedBlocks = blocks
      .map((block) => blockMapping[block])
      .filter(Boolean);

    const missingBlocks = DEFAULT_BLOCK_ARRAY.filter(
      (block) => !orderedBlocks.includes(block),
    );

    return [...orderedBlocks, ...missingBlocks];
  }

  return DEFAULT_BLOCK_ARRAY;
};

export function TemplatePageContent({
  content,
  menu,
  common,
}: TemplatePageContentProps) {
  const itemData = content.item;
  const html = itemData.text;

  const parkNavPage =
    itemData.ourObject &&
    itemData.ourObject.code === PARK_RAZVLECHENIY_CODE &&
    itemData.type === 'shop'
      ? {
          link: `/objects/${PARK_RAZVLECHENIY_CODE}/shops`,
          text: common.allShops,
        }
      : itemData.ourObject &&
          itemData.ourObject.code === PARK_RAZVLECHENIY_CODE &&
          itemData.type === 'restaurant'
        ? {
            link: `/objects/${PARK_RAZVLECHENIY_CODE}/restaurants`,
            text: common.allRestaurants,
          }
        : undefined;

  const entertainmentsPage =
    itemData.ourObject &&
    itemData.ourObject.code === PARK_RAZVLECHENIY_CODE &&
    (itemData.type === 'entertainment' ||
      itemData.type === 'attraction' ||
      itemData.type === 'show')
      ? {
          link: `/entertainments`,
          text: common.entertainments,
        }
      : undefined;

  const chipsRoutes = [
    {
      link: '/objects',
      text: common.objects,
    },
    ...(itemData.ourObject
      ? [
          {
            text: itemData.ourObject.name,
            link: `/objects/${itemData.ourObject.code}`,
          },
        ]
      : []),
    ...(parkNavPage ? [parkNavPage] : []),
    ...(entertainmentsPage ? [entertainmentsPage] : []),
    {
      link: `${getCardRedirectUrl(itemData.type, itemData.code)}`,
      text: itemData.name,
    },
  ];

  useEffect(() => {
    const color =
      itemData.ourObject && itemData.ourObject.colorStyles
        ? itemData.ourObject.colorStyles
        : BLACK_COLOR;

    if (color) {
      const styleTag = document.createElement('style');
      styleTag.textContent = getTextColor(color);
      document.head.appendChild(styleTag);

      return () => {
        document.head.removeChild(styleTag);
      };
    }
  }, [itemData.ourObject]);

  const orderedBlocks = getBlockStructure(itemData.blocks);

  const breadcrumbs = buildBreadcrumbsFromRoutes({
    linkHome: common.linkHome,
    routes: chipsRoutes,
  });

  const productSchemaImage =
    itemData?.galleryMob?.[0] ?? itemData?.gallery?.[0] ?? itemData?.picture;

  const ogImage =
    productSchemaImage &&
    (productSchemaImage.startsWith('http://') ||
    productSchemaImage.startsWith('https://')
      ? productSchemaImage
      : BACKEND_URL
        ? `${BACKEND_URL}${productSchemaImage}`
        : productSchemaImage);

  const metaWithOgImage = {
    ...content.meta,
    picture: ogImage || content.meta?.picture,
  };

  return (
    <Layout data={menu} meta={metaWithOgImage}>
      <ProductSchema
        microdata={{ name: itemData.name, image: productSchemaImage }}
        description={itemData.text}
        objectName={itemData?.ourObject?.name}
        ratingValue={itemData.microRatingValue}
        ratingCount={itemData.microRatingCount}
        reviewCount={itemData.microReviewCount}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <main className={styles.page}>
        <PaginationRoutes routes={chipsRoutes} />
        <Gallery
          galleryMob={itemData.galleryMob}
          gallery={itemData.gallery}
          title={itemData.name}
          alert={itemData.alert}
          unaccompanied={itemData.unaccompanied}
          accompanied={itemData.accompaniedBy}
        />
        <div className="info">
          {html && <InnerHTML html={html} allowRerender />}
        </div>
        <div dangerouslySetInnerHTML={{ __html: itemData.ourObject.styles }} />
        {orderedBlocks.map((block) => {
          switch (block) {
            case 'mode':
              return (
                <>
                  {itemData.mode && itemData.mode.length > 0 && (
                    <div className={styles.time}>
                      <p className={cn(styles.timeTitle, 'custom-time-title')}>
                        {common.timeTitle}
                      </p>
                      {itemData.mode.map(
                        (
                          el: { name: string; value: string },
                          index: number,
                        ) => (
                          <p
                            key={el.name + index}
                            className={cn('custom-time-text')}
                          >
                            {el.name}: {el.value}
                          </p>
                        ),
                      )}
                    </div>
                  )}
                </>
              );
            case 'params':
              return (
                <>
                  {itemData.params && itemData.params.length > 0 && (
                    <ObjectParams params={itemData.params} common={common} />
                  )}
                </>
              );
            case 'textParams':
              return (
                <>
                  <div className={styles.textParams}>
                    {itemData.textParams &&
                      itemData.textParams.length > 0 &&
                      itemData.textParams.map(
                        (param: { name: string; value: string }) => (
                          <p key={param.name} className={styles.param}>
                            <b className={styles.paramName}>
                              {parseText(param.name)}:
                            </b>{' '}
                            {parseText(param.value)}
                          </p>
                        ),
                      )}
                    {itemData.rules && (
                      <Link
                        href={itemData.rules}
                        className={styles.rules}
                        target="_blank"
                      >
                        <span>{common.rulesTitle}</span>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.0026 14.6666C11.6845 14.6666 14.6693 11.6818 14.6693 7.99992C14.6693 4.31802 11.6845 1.33325 8.0026 1.33325C4.32071 1.33325 1.33594 4.31802 1.33594 7.99992C1.33594 11.6818 4.32071 14.6666 8.0026 14.6666Z"
                            fill="#FF4718"
                          />
                          <path
                            d="M8 10.6666L10.6667 7.99992L8 5.33325"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M5.33594 8H10.6693"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Link>
                    )}
                  </div>
                </>
              );
            case 'events':
              return (
                <>
                  {itemData.events && itemData.events.length > 0 && (
                    <EventsBlock
                      events={itemData.events}
                      code={itemData.code}
                    />
                  )}
                </>
              );
            case 'specialOffers':
              return (
                <>
                  {itemData.specialOffers &&
                    itemData.specialOffers.length > 0 && (
                      <SliderBlock
                        items={itemData.specialOffers}
                        cardType={CardType.Filled}
                        link=""
                        size={CardSize.M}
                        title={common.specialOffersTitle}
                        buttonType={ButtonType.Arrow}
                        tagPosition={TagPosition.Top}
                        mobileEffect={MobileEffect.Slider}
                        className={styles.container}
                      />
                    )}
                </>
              );
            case 'menu':
              return (
                <>
                  {itemData.menu && (
                    <Link
                      href={`${BACKEND_URL}${itemData.menu}`}
                      className={cn(styles.menuButton, 'custom-menu-button')}
                      target="_blank"
                    >
                      <span>{common.menuTitle}</span>
                      <img
                        loading="lazy"
                        src="/svg/external.svg"
                        alt="link"
                        className={styles.menuButtonImg}
                      />
                    </Link>
                  )}
                </>
              );
            case 'tickets':
              return (
                <>
                  {itemData.tickets && itemData.tickets.length > 0 && (
                    <Tickets
                      tickets={itemData.tickets}
                      title={common.ticketsTitle}
                    />
                  )}
                </>
              );
            case 'map':
              return (
                <>
                  {itemData.location && (
                    <LocationMap
                      img={itemData.location.picture}
                      file={itemData.location.file}
                      mobileImg={itemData.location.pictureMob}
                    />
                  )}
                </>
              );
            case 'thingsToDo':
              return (
                <>
                  {itemData.thingsToDo && itemData.thingsToDo.length > 0 && (
                    <SliderBlock
                      items={itemData.thingsToDo}
                      cardType={CardType.Panel}
                      link={`/objects/${PROMENAD_CODE}`}
                      size={CardSize.SM}
                      title={common.thingsToDoTitle}
                      mobileEffect={MobileEffect.Slider}
                    />
                  )}
                </>
              );
            default:
              return null;
          }
        })}
      </main>
    </Layout>
  );
}
